<script lang="ts">
  import Icon from "./Icon.svelte";
  import type { Command, Subcommand, Workflow, SearchResult } from "../lib/commands";

  interface AutocompleteItem {
    type: "command" | "subcommand" | "workflow" | "token";
    data: Command | Subcommand | Workflow | SearchResult;
    command?: string;
  }

  interface Props {
    items: AutocompleteItem[];
    selectedIndex: number;
    onSelect: (item: AutocompleteItem) => void;
  }

  let { items, selectedIndex, onSelect }: Props = $props();

  // Group items by type
  let groupedItems = $derived.by(() => {
    const groups: Record<string, AutocompleteItem[]> = {
      commands: [],
      workflows: [],
      tokens: [],
    };

    items.forEach((item) => {
      if (item.type === "command" || item.type === "subcommand") {
        groups.commands.push(item);
      } else if (item.type === "workflow") {
        groups.workflows.push(item);
      } else if (item.type === "token") {
        groups.tokens.push(item);
      }
    });

    return groups;
  });

  function getGlobalIndex(groupName: string, localIndex: number): number {
    let index = 0;
    const groupOrder = ["commands", "workflows", "tokens"];

    for (const name of groupOrder) {
      if (name === groupName) {
        return index + localIndex;
      }
      index += groupedItems[name].length;
    }
    return index;
  }

  function getIcon(item: AutocompleteItem): string {
    if (item.type === "command") {
      return (item.data as Command).icon;
    } else if (item.type === "subcommand") {
      return "chevron-right";
    } else if (item.type === "workflow") {
      return (item.data as Workflow).icon;
    } else if (item.type === "token") {
      return "coins";
    }
    return "hash";
  }
</script>

<div class="autocomplete">
  {#if groupedItems.commands.length > 0}
    <div class="group">
      <div class="group-header">Commands</div>
      {#each groupedItems.commands as item, i}
        {@const globalIdx = getGlobalIndex("commands", i)}
        <button
          class="item"
          class:selected={selectedIndex === globalIdx}
          onclick={() => onSelect(item)}
        >
          <div class="item-icon">
            <Icon name={getIcon(item)} size={16} />
          </div>
          <div class="item-content">
            {#if item.type === "command"}
              {@const cmd = item.data as Command}
              <span class="item-name">/{cmd.name}</span>
              <span class="item-desc">{cmd.description}</span>
              {#if cmd.subcommands.length > 0}
                <div class="item-tags">
                  {#each cmd.subcommands.slice(0, 4) as sub}
                    <span class="tag">{sub.name}</span>
                  {/each}
                </div>
              {/if}
            {:else if item.type === "subcommand"}
              {@const sub = item.data as Subcommand}
              <span class="item-name">/{item.command} {sub.name}</span>
              <span class="item-desc">{sub.description}</span>
            {/if}
          </div>
        </button>
      {/each}
    </div>
  {/if}

  {#if groupedItems.workflows.length > 0}
    <div class="group">
      <div class="group-header">
        <Icon name="zap" size={12} />
        Workflows
      </div>
      {#each groupedItems.workflows as item, i}
        {@const globalIdx = getGlobalIndex("workflows", i)}
        {@const workflow = item.data as Workflow}
        <button
          class="item"
          class:selected={selectedIndex === globalIdx}
          onclick={() => onSelect(item)}
        >
          <div class="item-icon workflow">
            <Icon name={getIcon(item)} size={16} />
          </div>
          <div class="item-content">
            <span class="item-name">{workflow.name}</span>
            <span class="item-desc">{workflow.description}</span>
          </div>
          {#if workflow.trending}
            <div class="trending-badge">
              <Icon name="trending-up" size={12} />
            </div>
          {/if}
          {#if workflow.source === "protocol"}
            <span class="source-badge">{workflow.protocol}</span>
          {/if}
        </button>
      {/each}
    </div>
  {/if}

  {#if groupedItems.tokens.length > 0}
    <div class="group">
      <div class="group-header">
        <Icon name="coins" size={12} />
        Tokens
      </div>
      {#each groupedItems.tokens as item, i}
        {@const globalIdx = getGlobalIndex("tokens", i)}
        {@const token = item.data as SearchResult}
        <button
          class="item"
          class:selected={selectedIndex === globalIdx}
          onclick={() => onSelect(item)}
        >
          <div class="item-icon token">
            <Icon name="coins" size={16} />
          </div>
          <div class="item-content">
            <div class="token-header">
              <span class="item-name">{token.name}</span>
              {#if token.description}
                <span class="token-price">{token.description}</span>
              {/if}
            </div>
            {#if token.address}
              <span class="item-address">{token.address}</span>
            {/if}
          </div>
          {#if token.verified}
            <div class="verified-badge">
              <Icon name="check" size={12} />
            </div>
          {/if}
        </button>
      {/each}
    </div>
  {/if}

  <div class="footer">
    <span><kbd>↑↓</kbd> Navigate</span>
    <span><kbd>↵</kbd> Select</span>
    <span><kbd>esc</kbd> Close</span>
  </div>
</div>

<style>
  .autocomplete {
    position: absolute;
    top: calc(100% + var(--space-2));
    left: 0;
    right: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-xl);
    overflow: hidden;
    z-index: 1000;
    animation: fadeInDown var(--transition-base) ease;
  }

  .group {
    padding: var(--space-2);
  }

  .group:not(:last-child) {
    border-bottom: 1px solid var(--border-subtle);
  }

  .group-header {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-3);
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
  }

  .item {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    width: 100%;
    padding: var(--space-3);
    border-radius: var(--radius-md);
    text-align: left;
    transition: all var(--transition-fast);
  }

  .item:hover,
  .item.selected {
    background: var(--bg-hover);
  }

  .item.selected {
    background: var(--accent-primary);
  }

  .item.selected .item-name {
    color: white;
  }

  .item.selected .item-desc,
  .item.selected .item-address {
    color: rgba(255, 255, 255, 0.7);
  }

  .item.selected .item-icon {
    color: white;
  }

  .item-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
  }

  .item.selected .item-icon {
    background: rgba(255, 255, 255, 0.2);
  }

  .item-icon.workflow {
    background: rgba(139, 92, 246, 0.1);
    color: var(--accent-secondary);
  }

  .item-icon.token {
    background: rgba(34, 197, 94, 0.1);
    color: var(--accent-success);
  }

  .item-content {
    flex: 1;
    min-width: 0;
  }

  .item-name {
    display: block;
    font-family: var(--font-mono);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
  }

  .item-desc {
    display: block;
    font-size: 0.8125rem;
    color: var(--text-secondary);
    margin-top: 2px;
  }

  .item-address {
    display: block;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--text-tertiary);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
    margin-top: var(--space-2);
  }

  .tag {
    padding: 2px 6px;
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    background: var(--bg-tertiary);
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
  }

  .item.selected .tag {
    background: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.9);
  }

  .token-header {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .token-price {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    padding: 2px 6px;
    border-radius: var(--radius-sm);
  }

  .item.selected .token-price {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  .trending-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: rgba(59, 130, 246, 0.1);
    border-radius: var(--radius-full);
    color: var(--accent-primary);
  }

  .item.selected .trending-badge {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  .source-badge {
    font-size: 0.6875rem;
    font-weight: 500;
    padding: 2px 8px;
    background: var(--bg-tertiary);
    border-radius: var(--radius-full);
    color: var(--text-tertiary);
  }

  .item.selected .source-badge {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  .verified-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: var(--accent-primary);
    border-radius: var(--radius-full);
    color: white;
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    padding: var(--space-2) var(--space-3);
    background: var(--bg-tertiary);
    border-top: 1px solid var(--border-subtle);
    font-size: 0.6875rem;
    color: var(--text-muted);
  }

  .footer kbd {
    font-size: 0.625rem;
    padding: 1px 4px;
    margin-right: 2px;
  }
</style>
